import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { fmtDate, money, statusSeverity, SEVERITY, payrollTypeLabel } from '@/components/payroll/payrollFmt';
import {
  AlertTriangle, AlertCircle, Bell, CalendarClock, ArrowRight, Clock, CheckCircle2, Wallet, History, Archive, RotateCcw,
} from 'lucide-react';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const SEV_ICON = { reminder: Bell, action: AlertCircle, critical: AlertTriangle, complete: CheckCircle2 };

const RISK_LABEL = {
  period_open: 'Payroll Period Open',
  closes_today: 'Closes Today — Preparation May Begin',
  preparation_due: 'Preparation Due Today',
  in_progress: 'Payroll in Progress',
  due_today: 'Due Today',
  deadline_may_be_missed: 'Deadline May Be Missed',
  past_due: 'Past Due',
  rollout: 'Initial Rollout',
  submitted: 'Submitted',
  closed: 'Closed',
};
const RiskBadge = ({ s }) => {
  const sev = SEVERITY[statusSeverity(s)];
  const Icon = SEV_ICON[statusSeverity(s)];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${sev.badge}`} data-testid="ov-risk-badge">
      <Icon size={11} />{RISK_LABEL[s] || 'Payroll Period Open'}
    </span>
  );
};

const CardField = ({ label, children, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <span className="block text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
    <div className="mt-1 text-base font-semibold text-[#0E2A47]">{children}</div>
  </div>
);

const RunCard = ({ c, onOpen, canMark, onMarkHistorical }) => (
  <div className="w-full rounded-xl border border-slate-200 bg-white p-5" data-testid={`ov-run-${c.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="text-lg font-bold text-[#0E2A47]">{payrollTypeLabel(c.group)}</div>
      <RiskBadge s={c.cycle_risk} />
    </div>
    <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3 xl:grid-cols-4">
      <CardField label="Payroll Period">{fmtDate(c.period_start)}–{fmtDate(c.period_end)}</CardField>
      <CardField label="Scheduled Payday">{fmtDate(c.nominal_payday)}</CardField>
      <CardField label="Expected Payment Date">{fmtDate(c.target_funding_date)}</CardField>
      <CardField label="Send to QuickBooks By">{fmtDate(c.qb_submission_cutoff)}</CardField>
      <CardField label="Employee-Handbook Payment Date">{fmtDate(c.next_available_banking_day)}</CardField>
      <CardField label="Status">
        <span className="inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{c.status_label}</span>
      </CardField>
      <CardField label="Assigned To">{c.responsible_role || '—'}</CardField>
      <CardField label="Next Required Step" span><span className="font-medium text-slate-600">{c.next_action || '—'}</span></CardField>
    </div>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
      <div className="text-sm text-slate-600">People and Companies <b className="text-[#0E2A47]">{c.payee_count}</b> · Gross <b className="text-[#0E2A47]">{money(c.final_gross)}</b></div>
      <div className="flex items-center gap-2">
        {canMark && (
          <button onClick={() => onMarkHistorical(c)} data-testid={`ov-mark-historical-${c.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <Archive size={14} />Already processed outside Total Pay
          </button>
        )}
        <button onClick={onOpen} data-testid={`ov-open-run-${c.id}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#123a63]">
          Open Payroll Run <ArrowRight size={15} />
        </button>
      </div>
    </div>
  </div>
);

const Metric = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`mt-1 text-xl font-bold ${value === 'Not Available' ? 'text-slate-400' : 'text-[#0E2A47]'}`}>{value}</div>
  </div>
);

const PayrollOverview = ({ meta, onOpen }) => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = React.useCallback(() => {
    axios.get(`${API}/payroll/overview`, auth(token))
      .then((r) => setData(r.data))
      .catch(() => setErr('Unable to load Payroll overview.'));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const markHistorical = async (c) => {
    const reason = window.prompt(
      `Mark "${payrollTypeLabel(c.group)} · ${fmtDate(c.period_start)}–${fmtDate(c.period_end)}" as already processed OUTSIDE Total Pay?\n\nThis removes it from Due Now, approval reminders and payment submission. It does NOT delete the record or change any amounts, and a Global Administrator can restore it later.\n\nReason (required):`);
    if (reason === null) return;
    if (!reason.trim()) { window.alert('A reason is required.'); return; }
    setBusyId(c.id);
    try {
      await axios.post(`${API}/payroll/runs/${c.id}/mark-historical`, { historical: true, reason: reason.trim() }, auth(token));
      load();
    } catch (e) { window.alert(e?.response?.data?.detail || 'Unable to mark period.'); }
    setBusyId(null);
  };

  const restoreHistorical = async (c) => {
    if (!window.confirm(`Restore "${payrollTypeLabel(c.group)} · ${fmtDate(c.period_start)}–${fmtDate(c.period_end)}" to active payroll tracking?`)) return;
    setBusyId(c.id);
    try {
      await axios.post(`${API}/payroll/runs/${c.id}/mark-historical`, { historical: false }, auth(token));
      load();
    } catch (e) { window.alert(e?.response?.data?.detail || 'Unable to restore period.'); }
    setBusyId(null);
  };

  if (err) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600" data-testid="ov-error">{err}</div>;
  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-slate-100" data-testid="ov-loading" />;

  const canMark = !!data.can_mark_historical;
  const f = data.financials || {};
  return (
    <div className="space-y-6" data-testid="payroll-overview">
      {/* Thin overview banner — supplied Total Pay image, angled TP icon kept fully
          visible on the right (object-contain right, no crop/stretch); live heading
          over the white left area. */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="payroll-overview-banner">
        <div className="relative h-[118px] sm:h-[148px]">
          <img src="/module-banners/totalpay-banner.webp" alt="" aria-hidden="true"
            className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="payroll-banner-img" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 30%, rgba(255,255,255,0.72) 46%, rgba(255,255,255,0) 66%)' }} aria-hidden="true" />
          <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 50%, rgba(255,255,255,0.82) 63%, rgba(255,255,255,0) 83%)' }} aria-hidden="true" />
          <div className="relative z-10 flex h-full flex-col justify-center px-6">
            <h1 className="text-[24px] sm:text-[32px] leading-tight font-extrabold tracking-tight text-[#0E2A47]" data-testid="payroll-overview-title">Payroll</h1>
          </div>
        </div>
      </div>

      {/* Due Now */}
      {data.due_now ? (
        <section data-testid="ov-due-now">
          {(() => {
            const sevKey = data.due_now.notice_severity || 'reminder';
            const sev = SEVERITY[sevKey];
            const Icon = SEV_ICON[sevKey];
            return (
              <div className={`mb-3 flex items-center gap-2 rounded-xl border px-4 py-3 ${sev.panel}`} data-testid="ov-due-banner">
                <Icon size={18} className={sev.accent} />
                <div className="text-base font-semibold" data-testid="ov-due-notice">{data.due_now.notice}</div>
              </div>
            );
          })()}
          <div className="space-y-3">
            {data.due_now.runs.map((c) => <RunCard key={c.id} c={c} onOpen={() => onOpen && onOpen('runs')} canMark={canMark} onMarkHistorical={markHistorical} />)}
          </div>
          {data.due_now.attention?.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="ov-attention">
              {data.due_now.attention.map((a, i) => {
                const sev = SEVERITY[a.severity || 'action'];
                const Icon = SEV_ICON[a.severity || 'action'];
                return (
                  <div key={i} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium ${sev.panel}`} data-testid={`ov-attention-${i}`}>
                    <Icon size={15} className={sev.accent} />
                    <span>{a.message}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="ov-due-none">
          No payroll is due right now.
        </section>
      )}

      {/* Financials */}
      <section data-testid="ov-financials">
        <div className="mb-3 flex items-center gap-1.5 text-base font-semibold text-[#0E2A47]"><Wallet size={16} /> Current Payroll Financials</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Metric label="Payroll Total" value={money(f.payroll_total)} testid="ov-fin-total" />
          <Metric label="Budget" value={f.budget === null ? 'Not Available' : money(f.budget)} testid="ov-fin-budget" />
          <Metric label="Actual" value={f.actual === null ? 'Not Available' : money(f.actual)} testid="ov-fin-actual" />
          <Metric label="Difference" value={f.variance === null ? 'Not Available' : money(f.variance)} testid="ov-fin-variance" />
          <Metric label="Work-Order Pay" value={money(f.work_order_pay)} testid="ov-fin-wo" />
        </div>
      </section>

      {/* Already processed outside Total Pay (historical) */}
      {data.historical?.length > 0 && (
        <section data-testid="ov-historical">
          <div className="mb-3 flex items-center gap-1.5 text-base font-semibold text-[#0E2A47]"><Archive size={16} /> Already Processed Outside Total Pay</div>
          <div className="space-y-2">
            {data.historical.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`ov-historical-${c.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-[#0E2A47]">{payrollTypeLabel(c.group)} · {fmtDate(c.period_start)}–{fmtDate(c.period_end)}</div>
                  <div className="mt-0.5 text-xs text-slate-500">Marked already processed{c.historical_marked_by_name ? ` by ${c.historical_marked_by_name}` : ''}{c.historical_marked_at ? ` on ${fmtDate(c.historical_marked_at)}` : ''}. Excluded from Due Now, reminders &amp; submission.{c.historical_reason ? ` Reason: ${c.historical_reason}` : ''}</div>
                </div>
                {canMark && (
                  <button onClick={() => restoreHistorical(c)} disabled={busyId === c.id} data-testid={`ov-restore-historical-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    <RotateCcw size={14} />Restore to active
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming */}
      <section data-testid="ov-upcoming">
        <div className="mb-3 flex items-center gap-1.5 text-base font-semibold text-[#0E2A47]"><CalendarClock size={16} /> Upcoming Payroll</div>
        {data.upcoming ? (
          <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm" data-testid="ov-upcoming-card">
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold text-[#0E2A47]">{payrollTypeLabel(data.upcoming.group)}</div>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">{data.upcoming.note}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
              <div><span className="block text-xs font-medium uppercase tracking-wide text-slate-400">Payroll Period</span><div className="mt-1 text-base font-semibold text-[#0E2A47]">{fmtDate(data.upcoming.period_start)}–{fmtDate(data.upcoming.period_end)}</div></div>
              <div><span className="block text-xs font-medium uppercase tracking-wide text-slate-400">Scheduled Payday</span><div className="mt-1 text-base font-semibold text-[#0E2A47]">{fmtDate(data.upcoming.nominal_payday)}</div></div>
              <div><span className="block text-xs font-medium uppercase tracking-wide text-slate-400">Send to QuickBooks By</span><div className="mt-1 text-base font-semibold text-[#0E2A47]">{fmtDate(data.upcoming.qb_submission_cutoff)}</div></div>
              <div><span className="block text-xs font-medium uppercase tracking-wide text-slate-400">Preparation Begins</span><div className="mt-1 flex items-center gap-1 text-base font-semibold text-[#0E2A47]"><Clock size={14} />{fmtDate(data.upcoming.preparation_begins)}</div></div>
            </div>
          </div>
        ) : <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400" data-testid="ov-upcoming-none">Not Available</div>}
      </section>

      {/* History */}
      <section data-testid="ov-history">
        <div className="mb-3 flex items-center gap-1.5 text-base font-semibold text-[#0E2A47]"><History size={16} /> Completed Payroll — Previous Two Months</div>
        {data.history_available ? (
          <div className="space-y-3">
            {data.history.map((h) => (
              <div key={`${h.year}-${h.month}`} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ov-history-${h.year}-${h.month}`}>
                <div className="text-base font-semibold text-[#0E2A47]">{h.month_label}</div>
                <div className="mt-2 space-y-1.5 text-sm text-slate-600">
                  {h.runs.map((c) => (
                    <div key={c.id} className="flex items-center justify-between">
                      <span><CheckCircle2 size={13} className="mr-1 inline text-emerald-600" />{payrollTypeLabel(c.group)} · {fmtDate(c.period_start)}–{fmtDate(c.period_end)}</span>
                      <span>Gross <b>{money(c.final_gross)}</b></span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400" data-testid="ov-history-none">Not Available — no completed payroll runs yet.</div>}
      </section>
    </div>
  );
};

export default PayrollOverview;
